// src/components/patient/appointments/AppointmentForms.jsx
//
// Appointment forms by stage (Patient Appointments Task 3).
//
//   * the schema shown is the PINNED version the server returns; validation is
//     repeated server-side and its field errors are attached to the inputs;
//   * drafts are saved on the server, so they survive a refresh;
//   * a background refresh never overwrites input the patient is editing;
//   * files go presign -> PUT -> confirm and are stored in answers only as an
//     opaque reference; downloads ask the server for a short-lived link;
//   * forms the patient may only view render read-only.

import React, { useEffect, useRef, useState } from "react";

import { readApiError } from "../../../lib/apiErrors";
import { appointmentWorkspaceApi } from "../../../services/patientAppointmentWorkspace";
import { Pill } from "../booking/bookingUi";

const STAGES = [
  ["before_appointment", "Before your appointment"],
  ["during_appointment", "During your appointment"],
  ["after_appointment", "After your appointment"],
];

function fieldsOf(schema) {
  const out = [];
  (schema?.sections || []).forEach((section, index) => {
    out.push({ kind: "section", title: section.title, key: `section-${index}` });
    (section.fields || []).forEach((field) => out.push({ kind: "field", field }));
  });
  (schema?.fields || []).forEach((field) => out.push({ kind: "field", field }));
  return out;
}

function optionValues(field) {
  return (field.options || []).map((option) =>
    typeof option === "object" && option !== null
      ? { value: String(option.value ?? option.label ?? ""), label: String(option.label ?? option.value ?? "") }
      : { value: String(option), label: String(option) }
  );
}

function initialAnswers(assignment) {
  return { ...(assignment.draft?.answers || assignment.final_submission?.answers || {}) };
}

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.map((v) => (typeof v === "object" ? v.file_name || "File" : String(v))).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function FileField({ id, field, value, disabled, describedBy, invalid, appointmentId, assignmentId, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const files = Array.isArray(value) ? value : [];

  async function upload(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || busy) return;
    setBusy(true);
    setError("");
    try {
      const signed = await appointmentWorkspaceApi.presignFile(appointmentId, assignmentId, { fieldKey: field.key, file });
      await appointmentWorkspaceApi.uploadToSignedUrl(signed, file);
      const confirmed = await appointmentWorkspaceApi.confirmFile(appointmentId, assignmentId, {
        fieldKey: field.key,
        storageReference: signed.storage_reference,
        fileName: file.name,
      });
      onChange([...files, confirmed]);
    } catch (err) {
      setError(err?.uploadFailed ? "The file could not be uploaded. Please try again." : readApiError(err, "The file could not be uploaded."));
    } finally {
      setBusy(false);
    }
  }

  async function download(reference) {
    try {
      const link = await appointmentWorkspaceApi.fileDownloadUrl(appointmentId, assignmentId, reference);
      window.open(link.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(readApiError(err, "The file could not be opened."));
    }
  }

  return (
    <div>
      <ul className="space-y-1">
        {files.map((file) => (
          <li key={file.storage_reference} className="flex flex-wrap items-center gap-2 text-sm">
            <button type="button" onClick={() => download(file.storage_reference)} className="font-semibold text-sky-700 underline">
              {file.file_name || "Attached file"}
            </button>
            {!disabled ? (
              <button type="button" onClick={() => onChange(files.filter((f) => f.storage_reference !== file.storage_reference))} className="text-xs text-rose-700">
                Remove
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {!disabled ? (
        <input id={id} type="file" onChange={upload} disabled={busy} aria-describedby={describedBy} aria-invalid={invalid || undefined} className="mt-2 block text-sm" />
      ) : null}
      {busy ? <p className="mt-1 text-xs text-slate-500" aria-live="polite">Uploading…</p> : null}
      {error ? <p role="alert" className="mt-1 text-xs font-semibold text-rose-700">{error}</p> : null}
    </div>
  );
}

function FieldInput({ field, value, onChange, disabled, error, appointmentId, assignmentId }) {
  const id = `field-${assignmentId}-${field.key}`;
  const errorId = `${id}-error`;
  const hintId = field.help_text ? `${id}-hint` : undefined;
  const describedBy = [error ? errorId : null, hintId].filter(Boolean).join(" ") || undefined;
  const common = {
    id,
    disabled,
    "aria-invalid": error ? "true" : undefined,
    "aria-describedby": describedBy,
    "aria-required": field.required || undefined,
    className: "mt-1 w-full rounded-2xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300 disabled:bg-slate-50",
  };
  const type = String(field.type || "text");
  const label = field.label || field.title || field.key;
  const options = optionValues(field);

  let control;
  if (type === "textarea") {
    control = <textarea {...common} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  } else if (type === "select") {
    control = (
      <select {...common} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else if (type === "radio" || (type === "checkbox" && options.length)) {
    const multiple = type === "checkbox";
    const selected = multiple ? (Array.isArray(value) ? value : []) : value;
    control = (
      <fieldset className="mt-1" aria-describedby={describedBy} aria-invalid={error ? "true" : undefined}>
        <legend className="sr-only">{label}</legend>
        {options.map((o) => (
          <label key={o.value} className="mr-4 inline-flex items-center gap-2 text-sm">
            <input
              type={multiple ? "checkbox" : "radio"}
              name={id}
              value={o.value}
              disabled={disabled}
              checked={multiple ? selected.includes(o.value) : selected === o.value}
              onChange={(e) => onChange(multiple ? (e.target.checked ? [...selected, o.value] : selected.filter((v) => v !== o.value)) : o.value)}
            />
            {o.label}
          </label>
        ))}
      </fieldset>
    );
  } else if (type === "checkbox") {
    control = (
      <input id={id} type="checkbox" disabled={disabled} checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)}
        aria-invalid={error ? "true" : undefined} aria-describedby={describedBy} className="mt-2 h-4 w-4" />
    );
  } else if (type === "file") {
    control = (
      <FileField id={id} field={field} value={value} disabled={disabled} describedBy={describedBy} invalid={Boolean(error)}
        appointmentId={appointmentId} assignmentId={assignmentId} onChange={onChange} />
    );
  } else {
    const inputType = { number: "number", email: "email", phone: "tel", date: "date", time: "time", datetime: "datetime-local" }[type] || "text";
    control = <input {...common} type={inputType} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }

  return (
    <div className="mt-4">
      <label htmlFor={type === "radio" || (type === "checkbox" && options.length) ? undefined : id} className="text-sm font-semibold">
        {label}
        {field.required ? (
          <>
            <span aria-hidden="true" className="ml-0.5 text-rose-600">*</span>
            <span className="sr-only"> (required)</span>
          </>
        ) : null}
      </label>
      {field.help_text ? <p id={hintId} className="text-xs text-slate-500">{field.help_text}</p> : null}
      {control}
      {error ? <p id={errorId} role="alert" className="mt-1 text-xs font-semibold text-rose-700">{error}</p> : null}
    </div>
  );
}

function FormCard({ appointmentId, assignment, onSaved, onDirtyChange }) {
  const editable = assignment.access === "edit";
  const [answers, setAnswers] = useState(() => initialAnswers(assignment));
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [failure, setFailure] = useState("");
  const inFlight = useRef(false);
  const baseline = JSON.stringify(initialAnswers(assignment));

  // Adopt server data only when the patient has no unsaved edits.
  useEffect(() => {
    if (!dirty) setAnswers(JSON.parse(baseline));
  }, [baseline, dirty]);

  useEffect(() => {
    onDirtyChange?.(assignment.id, dirty);
  }, [assignment.id, dirty, onDirtyChange]);

  function update(key, value) {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
    setMessage("");
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function save(final) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(final ? "submit" : "draft");
    setFailure("");
    setMessage("");
    try {
      const result = final
        ? await appointmentWorkspaceApi.submitForm(appointmentId, assignment.id, answers)
        : await appointmentWorkspaceApi.saveDraft(appointmentId, assignment.id, answers);
      setErrors({});
      setDirty(false);
      setMessage(final ? "Form submitted." : "Draft saved.");
      onSaved?.(result.assignment);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const fieldErrors = {};
      (detail?.errors || []).forEach((e) => {
        if (e?.field) fieldErrors[e.field] = e.message;
      });
      setErrors(fieldErrors);
      setFailure(readApiError(err, final ? "The form could not be submitted." : "The draft could not be saved."));
    } finally {
      inFlight.current = false;
      setBusy("");
    }
  }

  const statusTone = assignment.status === "submitted" ? "success" : assignment.status === "in_progress" ? "info" : "warn";
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`form-${assignment.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold">{assignment.form_name}</h4>
          <div className="mt-1 flex flex-wrap gap-2">
            <Pill tone={statusTone}>{assignment.status === "submitted" ? "Submitted" : assignment.status === "in_progress" ? "Draft saved" : "Not started"}</Pill>
            {assignment.is_required ? <Pill tone="neutral">Required</Pill> : null}
            {!editable ? <Pill tone="neutral">View only</Pill> : null}
          </div>
        </div>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="rounded-2xl border border-slate-300 px-3 py-1.5 text-sm font-semibold">
          {open ? "Hide" : editable ? "Open form" : "View"}
        </button>
      </div>

      {open ? (
        <form onSubmit={(e) => { e.preventDefault(); save(true); }} noValidate className="mt-2">
          {failure ? <p role="alert" className="mt-2 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{failure}</p> : null}
          {!editable && assignment.final_submission && !fieldsOf(assignment.form_schema).some((item) => item.kind === "field") ? (
            // After submission only answers the form's author made visible are
            // returned by the server; there may be none.
            <p className="mt-3 text-sm text-slate-600">
              Your answers were submitted. None of them are set to be shown here.
            </p>
          ) : null}
          {fieldsOf(assignment.form_schema).map((item) =>
            item.kind === "section" ? (
              item.title ? <h5 key={item.key} className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">{item.title}</h5> : null
            ) : item.field.type === "section_header" ? null : editable ? (
              <FieldInput key={item.field.key} field={item.field} value={answers[item.field.key]} onChange={(v) => update(item.field.key, v)}
                disabled={Boolean(busy)} error={errors[item.field.key]} appointmentId={appointmentId} assignmentId={assignment.id} />
            ) : (
              <div key={item.field.key} className="mt-3 text-sm">
                <div className="font-semibold">{item.field.label || item.field.key}</div>
                {item.field.type === "file" ? (
                  <FileField field={item.field} value={answers[item.field.key]} disabled appointmentId={appointmentId} assignmentId={assignment.id} onChange={() => {}} />
                ) : (
                  <div className="text-slate-700">{displayValue(answers[item.field.key])}</div>
                )}
              </div>
            )
          )}
          {message ? <p role="status" className="mt-3 text-sm font-semibold text-emerald-700">{message}</p> : null}
          {editable ? (
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => save(false)} disabled={Boolean(busy)} aria-busy={busy === "draft" || undefined} className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
                {busy === "draft" ? "Saving…" : "Save draft"}
              </button>
              <button type="submit" disabled={Boolean(busy)} aria-busy={busy === "submit" || undefined} className="rounded-2xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {busy === "submit" ? "Submitting…" : "Submit form"}
              </button>
            </div>
          ) : null}
        </form>
      ) : null}
    </article>
  );
}

export default function AppointmentForms({ appointmentId, data, loading, error, onRetry, onSaved, onDirtyChange }) {
  if (loading && !data) return <p className="text-sm text-slate-500" aria-live="polite">Loading forms…</p>;
  if (error && !data) {
    return (
      <div role="alert" className="flex items-center justify-between gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
        <span>{readApiError(error, "Forms could not be loaded.")}</span>
        <button type="button" onClick={onRetry} className="rounded-xl border border-rose-300 bg-white px-3 py-1 text-sm font-semibold">Retry</button>
      </div>
    );
  }
  const groups = data?.groups || {};
  const total = STAGES.reduce((n, [stage]) => n + (groups[stage]?.length || 0), 0);
  if (!total) return <p className="text-sm text-slate-600">There are no forms for this appointment.</p>;
  return (
    <div className="space-y-5" data-testid="appointment-forms">
      {STAGES.map(([stage, title]) =>
        groups[stage]?.length ? (
          <section key={stage} aria-labelledby={`forms-${stage}`}>
            <h3 id={`forms-${stage}`} className="text-sm font-semibold text-slate-700">{title}</h3>
            <div className="mt-2 space-y-3">
              {groups[stage].map((assignment) => (
                <FormCard key={assignment.id} appointmentId={appointmentId} assignment={assignment} onSaved={onSaved} onDirtyChange={onDirtyChange} />
              ))}
            </div>
          </section>
        ) : null
      )}
    </div>
  );
}
